<template>
  <div
    class="guardrail-card"
    :class="{ 'is-locked': locked, 'read-only': readOnly }"
    :style="{ backgroundColor: ruleColor || '#ffeaa7' }"
    @click="handleCardClick($event)"
  >
    <a-pricing-strategy-card-rule-body :rule="guardrail.rule" />

    <!-- Chip del guardarail fijo -->
    <v-chip
      v-if="locked"
      x-small
      class="guardrail-fixed-chip font-weight-bold"
      :title="$t('guardrail_price_limits_hint')"
    >
      <v-icon x-small left>mdi-lock-outline</v-icon>
      {{ $t("guardrail_fixed") }}
    </v-chip>

    <!-- Acciones (visibles al hacer hover) -->
    <div class="card-actions" v-if="!readOnly && !locked">
      <v-btn
        icon
        x-small
        class="card-action"
        :title="$t('grid_card_delete')"
        @click.stop="showDeleteGuardrail()"
        @mousedown.stop
      >
        <v-icon x-small>mdi-delete</v-icon>
      </v-btn>
    </div>

    <!-- Orden de evaluación + asa de reordenación vertical.
         Van dentro de la card, no en una columna aparte, para que el ancho
         del guardarail siga cuadrando con las columnas de la grid. -->
    <div class="guardrail-lead d-flex align-center">
      <span
        class="guardrail-order-badge d-flex align-center justify-center font-weight-bold"
        :title="$t('guardrail_evaluation_order')"
      >
        <v-icon v-if="locked" size="12">mdi-lock-outline</v-icon>
        <template v-else>{{ orderIndex + 1 }}</template>
      </span>
      <div
        class="guardrail-drag-handle"
        v-if="!readOnly && !locked"
        :title="$t('guardrail_move')"
        @click.stop
      >
        <v-icon size="20">mdi-drag-horizontal</v-icon>
      </div>
    </div>

    <!-- Borrar guardarail -->
    <v-dialog
      v-model="show_delete_guardrail"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="500"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text class="text-center justify-center">
          <p>{{ $t("sure_delete_pricing_strategy_guardrail") }}</p>
          <div class="font-weight-bold">"{{ guardrail.rule.name }}"</div>
          <div
            v-if="strategiesCount > 0"
            v-html="
              $t('sure_delete_pricing_strategy_rule_count', {
                count: strategiesCount
              })
            "
            class="font-weight-medium mt-4"
          ></div>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="deleteGuardrail()"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideDeleteGuardrail()"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Card de un guardarail dentro de una estrategia.
 *
 * Comparte cuerpo visual con la card de regla (aPricingStrategyCardRuleBody),
 * pero ocupa siempre el 100% del ancho: los guardarailes se aplican a toda la
 * antelación, así que no hay handles de resize ni arrastre horizontal. El asa
 * sirve para reordenar verticalmente (el orden es el de evaluación).
 *
 * El guardarail fijo (locked) es el de precio mínimo/máximo: no se puede
 * borrar ni reordenar, pero sí editar como cualquier otro.
 */
import aPricingStrategyCardRuleBody from "@/components/pricing-strategy/aPricingStrategyCardRuleBody.vue";

export default {
  name: "a-pricing-strategy-card-strategy-guardrail",
  components: {
    aPricingStrategyCardRuleBody
  },
  props: {
    guardrail: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    // Posición en el orden de evaluación (0-based)
    orderIndex: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    show_delete_guardrail: false
  }),
  computed: {
    locked() {
      return !!this.guardrail.locked;
    },
    ruleColor() {
      return this.guardrail.rule.color;
    },
    strategiesCount() {
      return this.guardrail.rule.strategies_count || 0;
    }
  },
  methods: {
    showDeleteGuardrail() {
      this.show_delete_guardrail = true;
    },
    hideDeleteGuardrail() {
      this.show_delete_guardrail = false;
    },
    deleteGuardrail() {
      this.show_delete_guardrail = false;
      this.$emit("delete-card", this.guardrail.id);
    },
    handleCardClick(e) {
      if (this.readOnly) return;
      if (
        e.target.closest(".guardrail-drag-handle") ||
        e.target.closest(".card-actions")
      ) {
        return;
      }
      // Se emite el guardarail completo y no su rule_id: así no depende de
      // que la regla esté en el dataset de la biblioteca.
      this.$emit("edit-card", this.guardrail);
    }
  }
};
</script>

<style lang="scss" scoped>
.guardrail-card {
  position: relative;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  white-space: nowrap;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    .card-actions,
    .guardrail-drag-handle {
      opacity: 1;
    }
  }
  &.is-locked {
    opacity: 0.85;
    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      opacity: 1;
    }
  }
  &.read-only {
    cursor: default;
  }
}

.card-actions {
  position: absolute;
  top: 4px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.card-action {
  border-radius: 50% !important;
  background: rgba(255, 255, 255, 0.55) !important;
  backdrop-filter: blur(4px);
  &:hover {
    background: rgba(255, 255, 255, 0.85) !important;
  }
}

.guardrail-lead {
  position: absolute;
  top: 6px;
  left: 10px;
  gap: 4px;
  z-index: 10;
}

.guardrail-order-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 11px;
  line-height: 1;
  background: rgba(255, 255, 255, 0.7);
  color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
}

.guardrail-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s ease;
  &:active {
    cursor: grabbing;
  }
  .v-icon {
    color: rgba(0, 0, 0, 0.55);
  }
}

.guardrail-fixed-chip {
  position: absolute;
  top: 6px;
  right: 10px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(4px);
}
</style>
